import { useEffect, useRef, useState } from 'react';
import { storeService } from '../services/storeService';
import { StoreSeller, StoreProfile, PaymentMethod, CreateOrderPaymentMethodRequest } from '../models';
import { useCartStore } from '../store/cartStore';
import { useAuthStore } from '../store/authStore';
import { useToastStore } from '../store/toastStore';
import { EmptyState } from '../components/ui/EmptyState';
import { Button } from '../components/ui/Button';
import { Link, useNavigate } from 'react-router-dom';
import { buildWhatsAppUrl } from '../utils/whatsapp';
import { OrderSuccessScreen } from './checkout/OrderSuccessScreen';
import { StepIndicator } from './checkout/StepIndicator';
import { GuestInfoStep } from './checkout/GuestInfoStep';
import { PaymentStep } from './checkout/PaymentStep';
import { ConfirmStep } from './checkout/ConfirmStep';
import { ALL_PAYMENT_METHODS, GuestInfo, PaymentEntry, PaymentMode, Step } from './checkout/types';
import { getCampaignCatalogHref } from '../config/promotionCampaign';
import { useInitEffect } from '../hooks/useInitEffect';
import {
  setMetaPixelAdvancedMatching,
  trackAddPaymentInfo,
  trackCheckoutStep,
  trackInitiateCheckout,
  trackPurchase,
} from '../utils/metaPixel';

const PURCHASE_TRACKED_STORAGE_PREFIX = 'babychic-purchase-tracked-';

const CHECKOUT_STORAGE_KEY = 'babychic-checkout-state';

const DEFAULT_GUEST_INFO: GuestInfo = {
  name: '',
  phone: '',
  street: '',
  number: '',
  neighborhood: '',
  city: '',
  state: '',
  zipCode: '',
  complement: '',
  email: '',
  cpfCnpj: '',
  babies: [],
  lookupConfirmed: false,
};

interface PersistedCheckoutState {
  step?: Step;
  guestInfo?: GuestInfo;
  paymentEntries?: PaymentEntry[];
  paymentMode?: PaymentMode;
  observation?: string;
  selectedSellerId?: number;
}

function normalizeGuestInfo(guestInfo?: GuestInfo): GuestInfo | undefined {
  if (!guestInfo) return guestInfo;

  const rawBabies = (guestInfo as { babies?: unknown }).babies;
  const babies = Array.isArray(rawBabies) ? (rawBabies as GuestInfo['babies']) : [];

  return { ...guestInfo, babies, lookupConfirmed: guestInfo.lookupConfirmed === true };
}

function loadPersistedCheckoutState(): PersistedCheckoutState {
  try {
    const raw = sessionStorage.getItem(CHECKOUT_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    const result = parsed as PersistedCheckoutState;
    if ((result.step as string) === 'seller') result.step = 'payment';
    result.guestInfo = normalizeGuestInfo(result.guestInfo);
    return result;
  } catch {
    return {};
  }
}

export function CheckoutPage() {
  const { items, clearCart, totalPrice } = useCartStore();
  const hasPromotionalItems = items.some((i) => i.promotionalPrice != null);
  const { isAuthenticated, customerId } = useAuthStore();
  const navigate = useNavigate();
  const showToast = useToastStore((s) => s.show);

  const [persisted] = useState(loadPersistedCheckoutState);

  const [sellers, setSellers] = useState<StoreSeller[]>([]);
  const [loadingSellers, setLoadingSellers] = useState(true);
  const [step, setStep] = useState<Step>(() => persisted.step ?? (isAuthenticated ? 'payment' : 'guest-info'));
  const [selectedSeller, setSelectedSeller] = useState<StoreSeller | null>(null);
  const [customerProfile, setCustomerProfile] = useState<StoreProfile | null>(null);
  const [guestInfo, setGuestInfo] = useState<GuestInfo>(() => persisted.guestInfo ?? DEFAULT_GUEST_INFO);
  const [paymentEntries, setPaymentEntries] = useState<PaymentEntry[]>(() => {
    if (persisted.paymentEntries && persisted.paymentEntries.length > 0) {
      const restoredTotal = persisted.paymentEntries.reduce((sum, e) => sum + e.amountPaid, 0);
      if (Math.abs(restoredTotal - totalPrice()) < 0.01) {
        return persisted.paymentEntries;
      }
    }
    return [{ paymentMethod: PaymentMethod.Pix, amountPaid: totalPrice() }];
  });
  const [paymentMode, setPaymentMode] = useState<PaymentMode>(persisted.paymentMode ?? 'combine');
  const [observation, setObservation] = useState(persisted.observation ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [completedOrder, setCompletedOrder] = useState<{ orderId: number; whatsAppUrl: string } | null>(null);

  const isFirstRender = useRef(true);
  const wasAuthenticatedRef = useRef(isAuthenticated);
  const trackedStepRef = useRef<Step | null>(null);

  useInitEffect(() => {
    if (items.length === 0) return;
    trackInitiateCheckout(items);
  });

  useEffect(() => {
    if (items.length === 0 || trackedStepRef.current === step) return;
    const previousStep = trackedStepRef.current;
    trackedStepRef.current = step;
    trackCheckoutStep(step, items);
    if (previousStep === 'payment' && step === 'confirm') {
      trackAddPaymentInfo(items, paymentEntries);
    }
  }, [step, items, paymentEntries]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  useEffect(() => {
    if (wasAuthenticatedRef.current && !isAuthenticated) {
      setStep('guest-info');
      setSelectedSeller(null);
      setCustomerProfile(null);
      setGuestInfo(DEFAULT_GUEST_INFO);
      setPaymentEntries([{ paymentMethod: PaymentMethod.Pix, amountPaid: totalPrice() }]);
      setPaymentMode('combine');
      setObservation('');
    }
    wasAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated, totalPrice]);

  useEffect(() => {
    if (step === 'confirm' && !selectedSeller && !loadingSellers) {
      setStep('payment');
    }
  }, [step, selectedSeller, loadingSellers]);

  useEffect(() => {
    storeService
      .getSellers()
      .then((data) => {
        setSellers(data);
        const found = persisted.selectedSellerId
          ? data.find((s) => s.userId === persisted.selectedSellerId)
          : undefined;
        if (found) {
          setSelectedSeller(found);
        } else if (data.length === 1) {
          setSelectedSeller(data[0]);
        }
      })
      .finally(() => setLoadingSellers(false));
    if (customerId) {
      storeService.getProfile().then(setCustomerProfile).catch(() => {});
    }
  }, [customerId, persisted]);

  useEffect(() => {
    if (!hasPromotionalItems) return;
    setPaymentEntries((prev) =>
      prev.some((e) => e.installments != null && e.installments !== 1)
        ? prev.map((e) => (e.installments != null ? { ...e, installments: 1 } : e))
        : prev
    );
  }, [hasPromotionalItems]);

  useEffect(() => {
    try {
      const toSave: PersistedCheckoutState = {
        step,
        guestInfo,
        paymentEntries,
        paymentMode,
        observation,
        selectedSellerId: selectedSeller?.userId,
      };
      sessionStorage.setItem(CHECKOUT_STORAGE_KEY, JSON.stringify(toSave));
    } catch {
    }
  }, [step, guestInfo, paymentEntries, paymentMode, observation, selectedSeller]);

  if (completedOrder) {
    return (
      <OrderSuccessScreen
        orderId={completedOrder.orderId}
        whatsAppUrl={completedOrder.whatsAppUrl}
        onBackToStore={() => navigate('/')}
      />
    );
  }

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <EmptyState
          title="Carrinho vazio"
          action={<Link to={getCampaignCatalogHref()}><Button>Ver produtos</Button></Link>}
        />
      </div>
    );
  }

  const total = totalPrice();

  const totalPaid = paymentEntries.reduce((sum, e) => sum + e.amountPaid, 0);
  const paymentRemainder = total - totalPaid;

  const addPaymentMethod = () => {
    const used = new Set(paymentEntries.map((e) => e.paymentMethod));
    const next = ALL_PAYMENT_METHODS.find((pm) => !used.has(pm));
    if (!next) return;
    setPaymentEntries((prev) => [...prev, { paymentMethod: next, amountPaid: 0 }]);
  };

  const removePaymentMethod = (index: number) => {
    setPaymentEntries((prev) => prev.filter((_, i) => i !== index));
  };

  const updatePaymentEntry = (index: number, field: keyof PaymentEntry, value: number | PaymentMethod) => {
    setPaymentEntries((prev) =>
      prev.map((e, i) => {
        if (i !== index) return e;
        const updated = { ...e, [field]: value };
        if (field === 'paymentMethod') {
          updated.installments = (value as PaymentMethod) === PaymentMethod.CreditCard ? 1 : undefined;
        }
        if (field === 'installments' && hasPromotionalItems) {
          updated.installments = 1;
        }
        return updated;
      })
    );
  };

  const fillRemainder = (index: number) => {
    const othersPaid = paymentEntries
      .filter((_, i) => i !== index)
      .reduce((sum, e) => sum + e.amountPaid, 0);
    const remainder = Math.max(0, total - othersPaid);
    updatePaymentEntry(index, 'amountPaid', remainder);
  };

  const isPaymentValid = () => {
    if (paymentEntries.some((e) => e.amountPaid <= 0)) return false;
    return Math.abs(paymentRemainder) < 0.01;
  };

  const selectCombinePaymentMethod = (method: PaymentMethod) => {
    setPaymentEntries([{ paymentMethod: method, amountPaid: total }]);
  };

  const switchToDetailedPayment = () => {
    setPaymentMode('detailed');
  };

  const switchToCombinePayment = () => {
    setPaymentMode('combine');
    setPaymentEntries([{ paymentMethod: PaymentMethod.Pix, amountPaid: total }]);
  };

  const handleSubmit = async () => {
    if (!selectedSeller) return;
    setSubmitting(true);

    try {
      const paymentMethods: CreateOrderPaymentMethodRequest[] = paymentEntries.map((e) => ({
        paymentMethod: e.paymentMethod,
        amountPaid: e.amountPaid,
      }));

      const orderItems = items.map((i) => {
        const unitPrice = i.promotionalPrice ?? i.price;
        return {
          productId: i.productId,
          productVariantId: i.variantId,
          quantity: i.quantity,
          unitPrice,
          totalPrice: unitPrice * i.quantity,
          isGift: i.isGift ?? false,
        };
      });

      const isGuest = !isAuthenticated;

      const finalObservation = observation || '';

      const orderId = await storeService.createOrder({
        customerId: isGuest ? undefined : customerId!,
        sellerId: selectedSeller.userId,
        status: 6,
        channel: 5,
        saleDate: new Date().toISOString(),
        observation: finalObservation || undefined,
        items: orderItems,
        paymentMethods,
        ...(isGuest && {
          guestName: guestInfo.name,
          guestPhone: guestInfo.phone,
          guestStreet: guestInfo.street,
          guestNumber: guestInfo.number,
          guestNeighborhood: guestInfo.neighborhood,
          guestCity: guestInfo.city,
          guestState: guestInfo.state,
          guestZipCode: guestInfo.zipCode,
          guestComplement: guestInfo.complement || undefined,
          guestEmail: guestInfo.email || undefined,
          guestCpfCnpj: guestInfo.cpfCnpj || undefined,
          ...(guestInfo.babies.some((b) => b.name.trim())
            ? {
                guestBabies: guestInfo.babies
                  .filter((b) => b.name.trim())
                  .map((b) => ({
                    id: 0,
                    name: b.name.trim(),
                    birthdate: b.birthdate || undefined,
                    gender: b.gender ? Number(b.gender) : undefined,
                  })),
              }
            : {}),
        }),
      });

      const displayName = isGuest ? guestInfo.name : customerProfile?.name;
      const displayPhone = isGuest ? guestInfo.phone : customerProfile?.phone;
      const displayEmail = isGuest ? guestInfo.email : customerProfile?.email;

      try {
        const trackedKey = `${PURCHASE_TRACKED_STORAGE_PREFIX}${orderId}`;
        if (!sessionStorage.getItem(trackedKey)) {
          setMetaPixelAdvancedMatching({ email: displayEmail, phone: displayPhone });
          trackPurchase(orderId, items);
          sessionStorage.setItem(trackedKey, '1');
        }
      } catch {
      }

      const whatsAppUrl = buildWhatsAppUrl(selectedSeller, items, orderId, paymentEntries, displayName, displayPhone, observation || undefined, hasPromotionalItems);
      setCompletedOrder({ orderId, whatsAppUrl });
      clearCart();
      try {
        sessionStorage.removeItem(CHECKOUT_STORAGE_KEY);
      } catch {
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.errors?.[0] ||
        err?.response?.data?.message ||
        'Erro ao registrar pedido. Tente novamente.';
      showToast(msg, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmStepCustomerInfo: GuestInfo | undefined = !isAuthenticated
    ? guestInfo
    : customerProfile
    ? {
        name: customerProfile.name,
        phone: customerProfile.phone,
        street: customerProfile.street ?? '',
        number: customerProfile.number ?? '',
        neighborhood: customerProfile.neighborhood ?? '',
        city: customerProfile.city ?? '',
        state: customerProfile.state ?? '',
        zipCode: customerProfile.zipCode ?? '',
        complement: customerProfile.complement ?? '',
        email: customerProfile.email ?? '',
        cpfCnpj: '',
        babies: [],
        lookupConfirmed: false,
      }
    : undefined;

  const topBack =
    step === 'confirm'
      ? { label: 'Voltar', onClick: () => setStep('payment') }
      : step === 'payment' && !isAuthenticated
      ? { label: 'Voltar', onClick: () => setStep('guest-info') }
      : { label: 'Voltar para o carrinho', onClick: () => navigate('/cart') };

  return (
    <div className={`mx-auto px-4 py-4 sm:py-8 transition-all ${step === 'payment' ? 'max-w-4xl' : 'max-w-2xl'}`}>
      <button
        onClick={topBack.onClick}
        className="inline-flex items-center gap-2 text-sm font-medium text-text-medium hover:text-chocolate border border-champagne-dark hover:border-chocolate bg-white hover:bg-champagne px-4 py-2 rounded-full mb-3 sm:mb-6 transition-all duration-150"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        {topBack.label}
      </button>

      <div className="flex items-center gap-3 mb-3 sm:mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-text-dark">Finalizar pedido</h1>
      </div>

      <StepIndicator step={step} isAuthenticated={isAuthenticated} />

      {step === 'guest-info' && (
        <GuestInfoStep
          guestInfo={guestInfo}
          onChange={setGuestInfo}
          onNext={() => setStep('payment')}
        />
      )}

      {step === 'payment' && (
        <PaymentStep
          sellers={sellers}
          loadingSellers={loadingSellers}
          selectedSeller={selectedSeller}
          onSelectSeller={setSelectedSeller}
          items={items}
          total={total}
          paymentEntries={paymentEntries}
          paymentRemainder={paymentRemainder}
          paymentMode={paymentMode}
          hasPromotionalItems={hasPromotionalItems}
          onSelectCombineMethod={selectCombinePaymentMethod}
          onSwitchToDetailed={switchToDetailedPayment}
          onSwitchToCombine={switchToCombinePayment}
          onAddPaymentMethod={addPaymentMethod}
          onRemove={removePaymentMethod}
          onUpdate={updatePaymentEntry}
          onFillRemainder={fillRemainder}
          onBack={!isAuthenticated ? () => setStep('guest-info') : undefined}
          onNext={() => setStep('confirm')}
          isValid={isPaymentValid()}
        />
      )}

      {step === 'confirm' && selectedSeller && (
        <ConfirmStep
          seller={selectedSeller}
          paymentEntries={paymentEntries}
          total={total}
          observation={observation}
          onObservationChange={setObservation}
          items={items}
          onSubmit={handleSubmit}
          submitting={submitting}
          customerInfo={confirmStepCustomerInfo}
        />
      )}
    </div>
  );
}
